import { useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { jobListViewByCandidate } from "@endpoints/job.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import CandidateRowCard from "./components/CandidateRowCard";
import FilterSection, {
  EnumSortBy,
  IFilters,
  sortByFilters,
  sortOrderFilters,
} from "./components/FilterSection";
import { useDebounce } from "hooks/useDebounce";

const LIMIT = 25;

const ViewByCandidate = () => {
  const context: any = useOutletContext();
  const { searchText } = context;
  const scrollListRef = useRef(null);
  const sentinelRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [jobList, setJobList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<IFilters>({
    searchText: "",
    sortBy: sortByFilters[0],
    sortOrder: null,
  });

  const debouncedSearchText = useDebounce(filters?.searchText);
  const isSortOrderDisabled =
    filters?.sortBy?.value === EnumSortBy.MOST_LATEST_ACTIVITY;

  useEffect(() => {
    setFilters((prev) => {
      if (prev.sortBy?.value === EnumSortBy.MOST_LATEST_ACTIVITY) {
        return { ...prev, sortOrder: null };
      }

      if (prev.sortBy?.value === EnumSortBy.APPLIED_JOBS && !prev.sortOrder) {
        return { ...prev, sortOrder: sortOrderFilters[0] };
      }

      return prev;
    });
  }, [filters?.sortBy?.value]);

  const fetchJobs = useCallback(async () => {
    try {
      if (page > 1) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      const { data, success, pagination } = await jobListViewByCandidate({
        page,
        limit: LIMIT,
        searchText: debouncedSearchText,
        sortBy: filters?.sortBy?.value,
        sortOrder: isSortOrderDisabled ? undefined : filters?.sortOrder?.value,
      });
      if (success) {
        setJobList((prev) => (page === 1 ? data : [...prev, ...data]));
        setHasMore(Boolean(pagination?.hasMore));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [page, filters?.sortBy, filters?.sortOrder, debouncedSearchText]);

  const handleLoadMore = () => {
    if (hasMore) setPage((prev) => prev + 1);
  };

  // Init fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Page reset on searchText changed
  useEffect(() => {
    setPage(1);
  }, [searchText]);

  // Observer for the infinite scrolling
  useEffect(() => {
    if (!scrollListRef?.current || !sentinelRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: "0px",
        threshold: 1,
      },
    );

    observer.observe(sentinelRef?.current);

    return () => observer.disconnect();
  }, [isLoading, isLoadingMore]);

  return (
    <div className="min-h-0 flex flex-col flex-1 w-full">
      <FilterSection filters={filters} setFilters={setFilters} />
      {/* Table Header */}
      <div className="grid grid-cols-[1.4fr_1.1fr_0.5fr] gap-5 border-y px-7 py-3 bg-[#f8fafc] text-[#64748b]">
        <span className="uppercase flex-1 text-xss font-semibold tracking-wider">
          Candidate
        </span>
        <span className="uppercase flex-1 text-xss font-semibold tracking-wider">
          Latest Activity
        </span>
        <span className="uppercase flex-1 text-xss font-semibold tracking-wider">
          Applied Jobs
        </span>
      </div>
      {/* List section */}
      <div className="flex flex-col min-h-0 flex-1 overflow-y-scroll no-scrollbar">
        {isLoading ? (
          <div className="m-auto">
            <LoadingAnimation color="black" />
          </div>
        ) : jobList?.length ? (
          <div
            ref={scrollListRef}
            className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar"
          >
            {jobList?.map((job) => {
              return <CandidateRowCard data={job} />;
            })}
            {isLoadingMore ? (
              <div className="py-2 mx-auto font-medium text-light-black text-sm flex items-center gap-2">
                <LoadingAnimation color="black" /> Loading more data
              </div>
            ) : null}
            {/* Sentinel */}
            <div ref={sentinelRef} className="h-px" />
          </div>
        ) : (
          <div className="m-auto font-medium text-sm text-light-black">
            Candidates not available.
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewByCandidate;
